"use client";

import { useMemo } from "react";
import type { ActivityTimeSlotRow } from "../_logic/types/db-types";

interface RegisteredActivityState {
  /** Activity ids the viewer holds a REGISTERED (not waitlisted) row for. */
  registeredActivityIds: Set<number>;
  /** date -> count of the viewer's own REGISTERED rows on that date. */
  registeredCountByDate: Map<string, number>;
}

/**
 * Derives the activity ids and per-date counts of the viewer's registered (not waitlisted) rows
 * from an event-wide slot map, for the two evaluateRuleBlock inputs that useActivityRuleState
 * needs. Shared by ActivitiesRenderer and ScheduleDetailModal so the "registered AND NOT
 * waitlisted" filter — the invariant the day-cap/exclusion-group hint's correctness depends on —
 * lives in one place instead of two copies that could drift.
 *
 * @param timeSlotsByActivity - Event-wide slots grouped by activity id.
 * @param signedUpSlotIds - Slot ids the viewer holds any active (registered or waitlisted) row for.
 * @param waitlistedSlotIds - Subset of signedUpSlotIds the viewer is waitlisted (not registered) for.
 * @returns Registered activity ids and a date -> registered-count map.
 */
export function useRegisteredActivityIds(
  timeSlotsByActivity: Map<number, ActivityTimeSlotRow[]>,
  signedUpSlotIds: Set<number>,
  waitlistedSlotIds: Set<number>
): RegisteredActivityState {
  return useMemo(() => {
    const activityIds = new Set<number>();
    const countByDate = new Map<string, number>();
    for (const [activityId, slots] of timeSlotsByActivity) {
      for (const slot of slots) {
        if (!signedUpSlotIds.has(slot.id) || waitlistedSlotIds.has(slot.id)) continue;
        activityIds.add(activityId);
        if (slot.date) countByDate.set(slot.date, (countByDate.get(slot.date) ?? 0) + 1);
      }
    }
    return { registeredActivityIds: activityIds, registeredCountByDate: countByDate };
  }, [timeSlotsByActivity, signedUpSlotIds, waitlistedSlotIds]);
}
